import React, { useState } from 'react';
import { connect } from 'react-redux';
import { IconButton, Box, Grid, Typography, Autocomplete, TextField } from '@mui/material';
import { Cancel } from '@mui/icons-material';
import AttrGrid from './Grid';
import { updateLabelAction } from '../../../../redux/actions/labels';
import InlineTextEdit from '../../../util/InlineTextEdit';

const mapStateToProps = (state, ownProps) => ({
  matchingLabels: ownProps.labels ?? [],
  labelObjects: Object.values(state.entities.labels.byName),
});

const mapDispatchToProps = (dispatch, { pointId, equipId, equipSubId }) => ({
  updateLabel: (labelName, definition) =>
    dispatch(
      updateLabelAction(labelName, definition, {
        additions: { ptId: pointId, equipId, equipSubId },
      })
    ),
  removeLabel: (labelName, definition) =>
    dispatch(
      updateLabelAction(labelName, definition, {
        subtractions: { ptId: pointId, equipId, equipSubId },
      })
    ),
});

const RECENT_LABELS_KEY = 'recentCustomLabels';

const getRecentLabels = (labelNames) => {
  try {
    const saved = localStorage.getItem(RECENT_LABELS_KEY);
    const labels = JSON.parse(saved);
    return labels.filter((label) => labelNames.has(label));
  } catch (e) {
    return [];
  }
};

const saveRecentLabels = (labels) => {
  try {
    const toSave = labels;
    if (toSave.length > 20) {
      toSave.length = 20;
    }
    localStorage.setItem(RECENT_LABELS_KEY, JSON.stringify(toSave));
  } catch (e) {
    // nothing to do here
  }
};

const LabelsManager = ({ matchingLabels, labelObjects, updateLabel, removeLabel }) => {
  const [newLabelKey, setNewLabelKey] = useState('newLabel');
  const [newLabelErr, setNewLabelErr] = useState(null);
  const [recentLabels, setRecentLabels] = useState(
    getRecentLabels(new Set(labelObjects.map((l) => l.name)))
  );
  return (
    <AttrGrid alignItems="start">
      <Grid item xs={2}>
        <Typography variant="h6">Labels:</Typography>
      </Grid>
      <Grid item xs={6}>
        <Typography variant="h6">Definition:</Typography>
      </Grid>
      <Box width="100%" />
      {labelObjects
        .filter((label) => matchingLabels.includes(label.name))
        .map(({ name: labelName, definition }) => (
          <React.Fragment key={labelName}>
            <Grid item xs={2}>
              <IconButton
                onClick={() => {
                  removeLabel(labelName);
                }}
              >
                <Cancel size="small" />
              </IconButton>
              {labelName}
            </Grid>
            <Grid item xs={10}>
              <InlineTextEdit
                inputProps={{ maxLength: 2048 }}
                value={definition || ''}
                onValueChange={(s) => updateLabel(labelName, s)}
                fullWidth
              />
            </Grid>
            <Box width="100%" />
          </React.Fragment>
        ))}
      <React.Fragment key={newLabelKey}>
        <Grid item xs={2}>
          <Autocomplete
            options={Array.from(
              new Set(
                recentLabels
                  .concat(labelObjects.map((label) => label.name).sort())
                  .filter((labelName) => !matchingLabels.includes(labelName))
              )
            )}
            defaultValue=""
            onChange={(event, label) => {
              if (label) {
                if (matchingLabels.includes(label)) {
                  // Already exists, clear and inform user
                  setNewLabelErr(`Label '${label}' already present`);
                } else {
                  updateLabel(label);
                  recentLabels.unshift(label);
                  const newRecentLabels = Array.from(new Set(recentLabels));
                  setRecentLabels(newRecentLabels);
                  saveRecentLabels(newRecentLabels);
                  setNewLabelErr(null);
                }
              }
              // This is some jank to clear the field after the user submits (since it then gets created in the above list
              // We just want to twiddle the key, but if the key happens to match the current one, we want to set it to ANYTHING else
              if (label !== newLabelKey) {
                setNewLabelKey(label);
              } else if (newLabelKey !== '') {
                setNewLabelKey('');
              } else {
                setNewLabelKey('ANYTHING else');
              }
            }}
            // eslint-disable-next-line
            renderInput={(params) => <TextField label="New Label" {...params} />}
            fullWidth
            freeSolo
            clearOnBlur
            blurOnSelect
            selectOnFocus
          />
        </Grid>
        <Grid item xs={10}>
          <Typography color="error">{newLabelErr}</Typography>
        </Grid>
        <Box width="100%" />
      </React.Fragment>
    </AttrGrid>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(LabelsManager);
